/**
 * Authentication cache utilities for environment-specific cache management
 */

export interface CacheUtilsInterface {
  clearEnvironmentCache: (environment?: string) => void;
  getCurrentEnvironment: () => string;
  switchEnvironment: (newEnvironment: string) => void;
}

/**
 * Get the current environment identifier
 */
export const getCurrentEnvironment = (): string => {
  if (typeof window === 'undefined') return 'default';

  const envFromVar = process.env.NEXT_PUBLIC_ENV;
  if (envFromVar) return envFromVar;

  const hostname = window.location.hostname;
  if (hostname.includes('staging')) return 'staging';
  if (hostname.includes('localhost') || hostname.includes('dev')) return 'dev';
  return 'prod';
};

/**
 * Clear environment-specific cache entries
 */
export const clearEnvironmentCache = (environment?: string): void => {
  if (typeof window === 'undefined') return;

  const env = environment || getCurrentEnvironment();
  const prefix = `msal-${env}`;

  // Get all localStorage keys
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }

  // Remove environment-specific keys
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  console.info(
    `Cleared ${keysToRemove.length} cache entries for environment: ${env}`,
  );
};

/**
 * Switch to a new environment and clear old cache if needed
 */
export const switchEnvironment = (newEnvironment: string): void => {
  if (typeof window === 'undefined') return;

  const currentEnv = localStorage.getItem('msal-environment');

  if (currentEnv && currentEnv !== newEnvironment) {
    clearEnvironmentCache(currentEnv);
    console.info(
      `Switched from ${currentEnv} to ${newEnvironment} environment`,
    );
  }

  localStorage.setItem('msal-environment', newEnvironment);
};

/**
 * Clear all MSAL cache entries (for debugging)
 */
export const clearAllMsalCache = (): void => {
  if (typeof window === 'undefined') return;

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('msal-') || key.includes('msal'))) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
  localStorage.removeItem('msal-environment');

  console.info(`Cleared all MSAL cache entries (${keysToRemove.length} items)`);
};

export const CacheUtils: CacheUtilsInterface = {
  clearEnvironmentCache,
  getCurrentEnvironment,
  switchEnvironment,
};
